import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toggleMessagesVisible } from 'src/components/pages/Case/Case.slice';
import { handleErrorToast } from 'src/components/molecules/Toast';
import { emitTranslationInfo } from 'src/creators/socket';
import { useGetMessagesByReportQuery } from 'src/graphql';
import { AuditActions, MessageState } from 'src/types';
import { useAuditEntry } from '../useAuditEntry';
import { useSocketMessages } from './useSocketMessages';
import { useSocketSendMessage } from './useSocketSendMessage';

export const useReportMessages = () => {
  const dispatch = useDispatch();
  const [report, setReport] = useState<string | null>(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [translateMessages, setTranslateMessages] = useState(false);

  const { sendMessage: socketSend, markMessagesAsRead } =
    useSocketSendMessage();

  useEffect(() => {
    emitTranslationInfo({ translate: translateMessages });
  }, [translateMessages]);

  const { data: messages, isLoading: isMessagesLoading } =
    useGetMessagesByReportQuery(
      {
        report: report!,
        translate: translateMessages,
      },
      {
        enabled: report !== null,
      },
    );

  useSocketMessages({
    channel: report,
    translate: translateMessages,
    queryKey: 'GetMessagesByReport',
  });

  const markingAsReadRef = useRef(false);

  useEffect(() => {
    if (!report || !messages?.getMessagesByReport || markingAsReadRef.current) {
      return;
    }

    const hasUnreadMessages = messages.getMessagesByReport.some(
      (message) => message.state !== MessageState.READ && message.isReporter,
    );

    if (hasUnreadMessages && markMessagesAsRead(report)) {
      markingAsReadRef.current = true;
    }
  }, [report, messages, markMessagesAsRead]);

  useEffect(() => {
    if (!messages?.getMessagesByReport) {
      return;
    }

    const allRead = messages.getMessagesByReport.every(
      (message) => !message.isReporter || message.state === MessageState.READ,
    );

    if (allRead && markingAsReadRef.current) {
      markingAsReadRef.current = false;
    }
  }, [messages]);

  const { createAuditEntry, completeAuditEntry } = useAuditEntry();

  const openMessages = (channel: string) => {
    markingAsReadRef.current = false;
    setReport(channel);
    dispatch(toggleMessagesVisible({ visible: true, reportId: channel }));
    createAuditEntry(
      {
        auditAction: AuditActions.VIEW_REPORT_MESSAGES,
        auditData: {
          report: channel,
        },
        completed: false,
      },
      {
        onSuccess: ({ createAuditEntry }) => {
          if (createAuditEntry?.id) {
            completeAuditEntry({ id: createAuditEntry?.id });
          }
        },
      },
    );
  };

  const closeMessages = () => {
    dispatch(toggleMessagesVisible({ visible: false }));
    setReport(null);
    markingAsReadRef.current = false;
  };

  const sendMessage = (channel: string) => {
    if (!draftMessage) {
      return;
    }

    const draft = draftMessage;
    const sent = socketSend(
      channel,
      draft,
      () => setDraftMessage(''),
      () => setDraftMessage(draft),
    );
    if (!sent) {
      handleErrorToast({
        toastMessage: 'Oops, something went wrong. Please try again.',
      });
    }
  };

  return {
    draftMessage,
    setDraftMessage,
    translateMessages,
    setTranslateMessages,
    messages,
    openMessages,
    closeMessages,
    sendMessage,
    isMessagesLoading,
  };
};
